import React, { useState, useEffect } from "react";
import { X, Check, XCircle, ShieldOff } from "lucide-react";
import { useRemoteRegistry } from "../registry/RemoteRegistryContext";

interface Props {
  onClose: () => void;
}

export function ApprovalQueueModal({ onClose }: Props) {
  const { pendingApps, orgApps, approvePending, rejectPending, revokeApp, refreshPending } = useRemoteRegistry();
  const [activeTab, setActiveTab] = useState<"queue" | "approved">("queue");
  const [working, setWorking] = useState<string | null>(null);

  // Always fetch fresh data when the modal opens
  useEffect(() => {
    refreshPending();
  }, []);

  async function handleRevoke(id: string) {
    setWorking(id);
    try { await revokeApp(id); } finally { setWorking(null); }
  }

  async function handleApprove(id: string) {
    setWorking(id);
    try { await approvePending(id); } finally { setWorking(null); }
  }

  async function handleReject(id: string) {
    setWorking(id);
    try { await rejectPending(id); } finally { setWorking(null); }
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>App Review</h2>
          <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(activeTab === "queue" ? styles.tabActive : {}) }} onClick={() => setActiveTab("queue")}>
            Pending {pendingApps.length > 0 && <span style={styles.badge}>{pendingApps.length}</span>}
          </button>
          <button style={{ ...styles.tab, ...(activeTab === "approved" ? styles.tabActive : {}) }} onClick={() => setActiveTab("approved")}>
            Approved {orgApps.length > 0 && <span style={styles.badge}>{orgApps.length}</span>}
          </button>
        </div>

        {activeTab === "queue" && (
          <div style={styles.content}>
            {pendingApps.length === 0 ? (
              <p style={styles.empty}>No pending submissions.</p>
            ) : (
              pendingApps.map((app) => (
                <div key={app.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <span style={styles.cardLabel}>{app.label}</span>
                    <span style={styles.cardType}>{app.type}</span>
                  </div>
                  <a href={app.url} target="_blank" rel="noreferrer" style={styles.cardUrl}>{app.url}</a>
                  <div style={styles.cardMeta}>
                    Submitted by <strong>{app.submitted_by_name || app.submitted_by}</strong>
                    {" · "}{new Date(app.submitted_at).toLocaleDateString()}
                  </div>
                  <div style={styles.cardActions}>
                    <button
                      style={styles.approveBtn}
                      disabled={working === app.id}
                      onClick={() => handleApprove(app.id)}
                    >
                      <Check size={14} style={{ marginRight: 4 }} /> Approve
                    </button>
                    <button
                      style={styles.rejectBtn}
                      disabled={working === app.id}
                      onClick={() => handleReject(app.id)}
                    >
                      <XCircle size={14} style={{ marginRight: 4 }} /> Reject
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "approved" && (
          <div style={styles.content}>
            {orgApps.length === 0 ? (
              <p style={styles.empty}>No approved apps.</p>
            ) : (
              orgApps.map((app) => (
                <div key={app.name} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <span style={styles.cardLabel}>{app.label}</span>
                    <span style={styles.cardType}>{app.type}</span>
                  </div>
                  <a href={app.url} target="_blank" rel="noreferrer" style={styles.cardUrl}>{app.url}</a>
                  <div style={styles.cardActions}>
                    <button
                      style={styles.revokeBtn}
                      disabled={working === (app as any).id}
                      onClick={() => handleRevoke((app as any).id)}
                    >
                      <ShieldOff size={14} style={{ marginRight: 4 }} /> Revoke
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )} 

      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 8, width: 520, maxWidth: "90vw", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e0e0e0" },
  title: { fontSize: 18, fontWeight: 600, margin: 0 },
  closeBtn: { background: "none", border: "none", cursor: "pointer", color: "#5f6368", display: "flex", alignItems: "center", padding: 4 },
  tabs: { display: "flex", borderBottom: "1px solid #e0e0e0" },
  tab: { flex: 1, padding: "10px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#666", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 },
  tabActive: { color: "#136945", borderBottom: "2px solid #136945", fontWeight: 600 },
  badge: { background: "#e6f3ed", color: "#136945", borderRadius: 10, padding: "1px 7px", fontSize: 12, fontWeight: 700 },
  content: { padding: "16px 20px" },
  empty: { fontSize: 14, color: "#aaa", fontStyle: "italic" },
  card: { border: "1px solid #e0e0e0", borderRadius: 8, padding: "14px 16px", marginBottom: 12 },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 },
  cardLabel: { fontWeight: 600, fontSize: 15 },
  cardType: { fontSize: 11, background: "#f0f0f0", borderRadius: 4, padding: "2px 6px", color: "#555" },
  cardUrl: { fontSize: 12, color: "#136945", display: "block", marginBottom: 6, wordBreak: "break-all" as const },
  cardMeta: { fontSize: 12, color: "#888", marginBottom: 10 },
  cardActions: { display: "flex", gap: 8 },
  approveBtn: { display: "flex", alignItems: "center", background: "#e6f4ea", color: "#1e7e34", border: "1px solid #a8d5b5", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  rejectBtn: { display: "flex", alignItems: "center", background: "#fce8e6", color: "#c62828", border: "1px solid #f5c6cb", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  revokeBtn: { display: "flex", alignItems: "center", background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
};
